import { addQueryParamsToUrl } from 'lib/url/searchUrlUtils'

import { getImageUrl } from 'lib/image/imageUtils'
import { sendShareWithFriendRequest, sendShareWithFriendV2Request } from 'api/notification'
import config from 'constants/config'
import { getHolidaySynonymLabels } from 'hooks/useHolidaySynonymLabels'
import qs from 'qs'
import type { ShareVariant } from 'hooks/useShareVariant'
import type { IntlShape } from 'react-intl'

const SHARE_WITH_FRIEND_V2_OFFER_TYPES = ['hotel', 'tour', 'cruise', 'tour_v2', 'bedbank_hotel']

export interface ShareModalConfig {
  utmCampaign?: string
  pathname: string
  customerPortalURL: string
  offerLocation?: string
  memberId?: string
  offerId: string
  region: string
  type: string
  image?: App.Image
  queryString?: string
  offerType?: 'hotel' | 'tour' | 'cruise' | 'tour_v2' | 'bedbank_hotel'
  shareVariant?: ShareVariant
  intl: IntlShape
}

export function getShareModalConfig({
  utmCampaign,
  pathname,
  customerPortalURL,
  offerLocation,
  memberId,
  offerId,
  region,
  type,
  image,
  queryString = '',
  offerType,
  shareVariant,
  intl,
}: ShareModalConfig,
opts?: { [key: string]: any },
) {
  const utm = {
    utm_campaign: utmCampaign,
    utm_source: config.BRAND,
    utm_medium: 'web',
  }
  const removedRegionPathname = pathname.replace(/^\/[a-z]+/, '')
  const queryParams = qs.parse(queryString)
  const url = addQueryParamsToUrl(`${customerPortalURL}${removedRegionPathname}`, {
    ...utm,
    ...queryParams,
    share_variant: shareVariant ?? 'modalShare',
  })

  let title = getHolidaySynonymLabels(intl).yourNextHoliday
  if (offerLocation) {
    title = intl.formatMessage(
      {
        id: 'UaBXufDetR3AympDhfNC',
        defaultMessage: '{titleBase} in {offerLocation}',
        description: 'Share modal title with destination location appended',
      },
      {
        titleBase: title,
        offerLocation,
      },
    )
  }

  const useShareWithFriendV2 = config.BRAND === 'luxuryescapes' && offerType && SHARE_WITH_FRIEND_V2_OFFER_TYPES.includes(offerType)
  const typeForSend = config.agentHub.isEnabled && offerType === 'bedbank_hotel' ? 'bedbankOffer' : type

  return {
    url,
    utm_campaign: 'webshare',
    mailToSubject: encodeURIComponent(title),
    shareImg: image?.id && getImageUrl(image.id, { width: 650 }),
    shareTitle: title,
    shareMessage: intl.formatMessage({
      id: '02mUTZYZauTr7L8ak7mU',
      defaultMessage: 'Check out this amazing holiday...',
      description: 'Default share message used in share modal for sharing content',
    }),
    pageId: offerId || removedRegionPathname,
    pageType: type,
    ...(shareVariant !== undefined ? { shareVariant } : {}),
    onSend: async(state: { message?: string, friend_email: string }) => useShareWithFriendV2 ? sendShareWithFriendV2Request({
      offer_type: offerType,
      friend_email: state.friend_email,
      brand: config.BRAND,
      region,
      offer_id: offerId,
    }) : sendShareWithFriendRequest({
      type: typeForSend,
      offerId,
      memberId,
      location: offerLocation,
      region,
      utm,
      friendMessage: state.message,
      friendEmail: state.friend_email,
      heroImageId: image?.id,
    }),
    ...opts,
  }
}
